import { Route, Switch, Router } from "wouter";
import { useAuth } from "@/lib/platform/AuthProvider";
import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import { useConsent } from "@/lib/consent";
import { useProfile } from "@/lib/api/useProfile";
import { NavigationProvider, useAppNavigation } from "@/lib/platform";

// Pages
import Quiz from "./pages/Quiz";
import Onboarding from "./pages/Onboarding";
import { startOnboardingSession } from "@/lib/onboardingSession";
import {
  resolveOnboardingRoute,
  settleOnboardingDecision,
  type OnboardingDecision,
} from "./components/onboarding/surface";

// The onboarding experience name stamped on analytics and session rows. Kept
// from the `onboarding_flow` experiment so post-ship data lines up with the
// winning arm's.
const ONBOARDING_EXPERIENCE = "v3";
import Dashboard from "./pages/Dashboard";
import Connections from "./pages/Connections";
import Booking from "./pages/Booking";
import BookingStart from "./pages/BookingStart";
import BookingDetail from "./pages/BookingDetail";
import PollDetail from "./pages/PollDetail";
import ClubsIndex from "./pages/clubs/ClubsIndex";
import ClubDetail from "./pages/clubs/ClubDetail";
import ClubPostThread from "./pages/clubs/ClubPostThread";
import ClubForum from "./pages/clubs/ClubForum";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import EditPersonality from "./pages/EditPersonality";
import Settings from "./pages/Settings";
import NotificationSettings from "./pages/NotificationSettings";
import PostDinnerSurvey from "./pages/PostDinnerSurvey";
import Chat from "./pages/Chat";
import Migrate from "./pages/Migrate";
import StartPage from "./pages/StartPage";
import InviteAccept from "./pages/InviteAccept";
import HostEventCreate from "./pages/HostEventCreate";
import EventPublic from "@/pages/EventPublic";
import EventsIndex from "@/pages/EventsIndex";
import KikiPage from "@/pages/KikiPage";
import GroupPublic from "@/pages/GroupPublic";
import GroupEdit from "./pages/GroupEdit";

// Components
import Navigation from "./components/Navigation";
import ProtectedRoute from "./components/ProtectedRoute";
import TitledRoute from "@/components/TitledRoute";
import ParticleBackground from "@/components/ux/ParticleBackground";
import {
  ConversationsProvider
} from "./components/chat";
import { NativeAppInsets } from "./components/ux/NativeAppInsets";
import { ViewportDebug } from "./components/ux/ViewportDebug";
import { Loader } from "@/components/Loader";
import tracking from "@/lib/tracking";
import { getQuizAudience } from "@/lib/quizAudience";
import { usePlatform } from "@/lib/platform";
import { useAppViewportHeight } from "./lib/useAppViewport";
import { useT } from "@/lib/i18n";
import Terms from "@/pages/static/Terms";
import Privacy from "@/pages/static/Privacy";
import CommunityGuidelines from "@/pages/static/CommunityGuidelines";
import Help from "@/pages/static/Help";
import EventInterested from "@/pages/EventInterested";
interface MainAppProps {
  basePath?: string;
}

function MainApp({ basePath = "/app" }: MainAppProps) {
  return (
    <NavigationProvider basePath={basePath}>
      <MainAppContent basePath={basePath} />
    </NavigationProvider>
  );
}

/**
 * The three signup-adjacent URLs, rendered by one component.
 *
 * `/onboarding` is signup, `/quiz` is the answer editor, `/` sends signed-out
 * visitors to the first. Which of those a given request becomes is decided in
 * `components/onboarding/surface` and nowhere else — this component renders
 * the answer.
 *
 * One component for all three because they share a single invariant: nothing
 * unmounts when `isSignedIn` flips. The v3 flow creates the account *mid-way*,
 * and while signed-out and signed-in users were served different trees, auth
 * success unmounted the flow and remounted something else in its place, losing
 * the answers with it.
 */
function OnboardingRoute({ route }: { route: "onboarding" | "quiz" | "root" }) {
  const { isLoaded, isSignedIn } = useAuth();
  const { navigate } = useAppNavigation();

  const decision = resolveOnboardingRoute({ route, isLoaded, isSignedIn });

  // Onboarding is a session, not a page.
  //
  // Once the flow is on screen the route stops re-deciding, because the inputs
  // it decides on are exactly the ones the flow changes as you walk it. Signing
  // in flips `isSignedIn`; the autosave then completes the profile and stamps
  // `quizCompletedAt` — and re-running the entry rules on that ejected the user
  // to the dashboard from the middle of the flow they were finishing. Entry
  // rules answer "should this user start onboarding", which is a question with
  // one answer per visit, not one per render.
  //
  // The flow ends by navigating somewhere itself, so nothing here has to.
  const flowDecision = useRef<Extract<OnboardingDecision, { kind: "flow" }> | null>(
    null,
  );
  if (decision.kind === "flow") flowDecision.current = decision;
  const settled = settleOnboardingDecision(flowDecision.current, decision, route);

  const flowShown = settled.kind === "flow";

  // Reported once per visit. PostHog starts opted out until consent resolves
  // and a capture then is silently dropped, not queued, so this waits for
  // consent. The consent state is a dependency, so the grant re-runs it.
  const { state: consentState } = useConsent();
  const exposureReported = useRef(false);
  useEffect(() => {
    if (!flowShown || exposureReported.current) return;
    if (posthog.has_opted_out_capturing()) return;
    exposureReported.current = true;
    tracking.onboardingExperienceShown(ONBOARDING_EXPERIENCE, getQuizAudience());
  }, [flowShown, consentState.analytics]);

  // First-party session record (onboarding_sessions). Unlike the PostHog
  // event above this survives the consent race.
  const sessionStarted = useRef(false);
  useEffect(() => {
    if (!flowShown || sessionStarted.current) return;
    sessionStarted.current = true;
    startOnboardingSession({
      experience: ONBOARDING_EXPERIENCE,
      audience: getQuizAudience(),
    });
  }, [flowShown]);

  // Redirects are an effect, not a render: navigating during render is what
  // produced the loops this consolidation exists to remove.
  const redirectTo = settled.kind === "redirect" ? settled.to : null;
  useEffect(() => {
    if (!redirectTo) return;
    // Query carries the flow's own state (`?a=`, `?et=`, `?ff=`, `?step=`) and
    // the ad params the funnel is measured on. Dropping it loses both.
    const query = typeof window === "undefined" ? "" : window.location.search;
    navigate(`${redirectTo}${query}`);
  }, [redirectTo, navigate]);

  if (settled.kind === "loading" || settled.kind === "redirect") return null;

  if (settled.kind === "editor") return <Quiz />;

  // The flow deliberately does NOT take itself out of the document.
  //
  // Mobile Safari retracts its toolbars for document scrolling and nothing
  // else, so a `fixed` shell can never reclaim that strip however it is sized.
  // In the document it can: the flow is at least one *large* viewport tall, so
  // while the toolbars are up the page is taller than the window by at least
  // their height and the first swipe retracts them. See `.min-h-viewport` in
  // index.css — a floor rather than a fixed height, which keeps the document
  // the only scroller on the page. The dock rides along on `sticky`, so the
  // primary button is on screen before and after.
  //
  // No z-index: the chrome it would have needed to out-rank isn't rendered on
  // this route at all (see `onboardingHidesChrome`). Anything that should sit
  // above signup — dialogs, toasts, modals — is on the app's z-[9999] layer
  // and does so without this having to pick a number to lose to.
  return (
    <div className="min-h-viewport flex">
      {/* No safe-area padding here. The flow pins its own sticky bar to the
          top of the screen and pads it itself, because the inset has to sit
          inside the bar for its background to reach under the status bar.
          Padding the container as well pushed the whole flow down by the
          inset again. */}
      <NativeAppInsets insets={false}>
        <Onboarding variant={ONBOARDING_EXPERIENCE} />
      </NativeAppInsets>
    </div>
  );
}

function MainAppContent({ basePath }: { basePath: string }) {
  const { t } = useT();
  const { isLoaded, isSignedIn, openSignIn } = useAuth();
  const { isNative } = usePlatform();
  const { navigate, location } = useAppNavigation();
  const { navigation: navigationPlatform } = usePlatform();
  // Two separate questions, previously answered by one flag.
  //
  // Furniture — nav and the install banner — is not rendered on signup at all.
  // Not rendering it is the point: the alternative is leaving it there and
  // stacking above it, which is what the flow's `z-[60]` did, and it meant
  // every overlay in the app had to out-rank that number to be seen. The
  // sign-in dialog didn't, so "Log in" during signup opened a dialog behind
  // the flow. Keyed off the route only, never off auth: the v3 flow signs
  // users in half way through, so `{isSignedIn && <Navigation />}` would
  // otherwise appear on top of it mid-flow.
  const onboardingHidesChrome = location.startsWith("/onboarding");
  // The page background is a different question with, today, the same
  // answer: the flow paints its own opaque canvas, so the particle field
  // behind it is invisible work.
  const onboardingOwnsBackground = onboardingHidesChrome;

  // Chat is an app screen, not a document: its header and composer are pinned
  // to the edges of the window and only the message list moves. That needs a
  // *definite* height to hang off — `min-h-dvh` gives a floor, and a floor is
  // exactly what let the page grow behind the chat and scroll underneath it,
  // so a swipe moved either the messages or the whole page depending on where
  // it landed. Here the shell is sized to the window and clips, which leaves
  // the message list as the only thing on the page that scrolls.
  const routeOwnsViewport = location === "/chat" || location.startsWith("/chat/");
  useAppViewportHeight(routeOwnsViewport);

  useEffect(() => {
    tracking.trackPage("App");
  }, []);

  useEffect(() => {
    navigationPlatform.onDeepLink((path) => {
      console.log('App Deep link: ', path);
      if (/^https?:\/\//.test(path) || path.startsWith('outclose://')) {
        const u = new URL(path);
        path = u.pathname + u.search + u.hash;
      }
      // Normalize path to remove /app prefix for deep links
      if (path.startsWith('/app')) {
        path = path.substring(4);
      }
      navigate(path);
    });
  }, [navigationPlatform, navigate]);

  // Redirect if basePath isn't /app but location starts with /app/ (Workaround for different subdomains)
  useEffect(() => {
    if (basePath !== "/app") {
      const pathName = window.location.pathname;
      if (pathName.startsWith("/app/")) {
        const newPath = window.location.pathname.substring(4);
        window.location.replace(
          newPath + window.location.search + window.location.hash
        );
      } else if (pathName == "/app") {
        window.location.replace(basePath);
      }
    }
  }, [basePath, location]);

  // react to sso_redirect hash parameter (Example: https://app.outclose.com/#/sso-callback?redirect_url=https%3A%2F%2Fapp.outclose.com%2Fapp%2Fquiz%3FsaveAnswers)
  useEffect(() => {
    try {
      const hash = window.location.hash;
      if (hash.startsWith('#/sso_redirect?redirect_url=')) {
        const redirectUrl = decodeURIComponent(hash.split('?redirect_url=')[1]);
        window.location.hash = '';
        window.location.replace(redirectUrl);
      }
    } catch (error) {
      console.error('Error processing sso_redirect hash parameter:', error);
    }
  }, [location]);


  // Check for Apple account migration requirement
  const { data: profileData } = useProfile();
  const hasRedirectedToMigrateRef = useRef(false);
  
  useEffect(() => {
    if (!isSignedIn || hasRedirectedToMigrateRef.current) return;
    if (!profileData?.success || !profileData.data) return;
    
    const { needsMigration, mergePassword } = profileData.data as any;
    
    // If user needs migration and we're not already on the migrate page
    if (needsMigration && mergePassword && !location.includes("/migrate")) {
      hasRedirectedToMigrateRef.current = true;
      // Store password and redirect to migrate page
      navigate("/migrate?code=" + mergePassword);
    }
  }, [isSignedIn, profileData, location, navigate]);

  // Auto-open non-dismissable sign-in overlay for unauthenticated users on non-quiz routes
  useEffect(() => {
    if (isLoaded && !isSignedIn && !isNative) {
      // Onboarding carries its own account step, so the modal would be a
      // second sign-in competing with it — and `preventClose: true` means one
      // the user cannot dismiss to get back to the flow. `/onboarding` was
      // missed when it was split out of `/quiz`; the native StartPage guard
      // below got the same exemption at the time, this one did not.
      const isQuizOrHome =
        location === "/" ||
        location.startsWith("/quiz") ||
        location.startsWith("/onboarding");
      const isMigrate = location.startsWith("/migrate");
      // The /invite/:code page has its own sign-in CTA and shows a preview
      // first — don't shove the modal in the user's face before they see it.
      const isInvite = location.startsWith("/invite/");
      // Public event surfaces — /events (directory) and /events/:slug
      // (individual) — are shareable artifacts; visitors should land on them
      // without an auth wall. Sign-in happens at RSVP time instead. The legacy
      // /e slug is kept so old shared links bypass the wall while redirecting.
      const isPublicEvent =
        location === "/events" ||
        location.startsWith("/events/");
      if (!isQuizOrHome && !isMigrate && !isInvite && !isPublicEvent) {
        openSignIn({ preventClose: true });
      }
    }
  }, [isLoaded, isSignedIn, isNative, location, openSignIn]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (location.includes("/migrate")) {
    return <div className="h-dvh min-h-dvh relative flex">
        <NativeAppInsets>
          <ParticleBackground className="absolute inset-0" />
          <Migrate />
        </NativeAppInsets>
       </div>
  }

  // Not signed in: show Quiz for / and /quiz, StartPage on native, overlay for other routes
  if (!isSignedIn) {
    // Native: non-quiz routes show StartPage (but allow /invite/:code preview
    // and the public /events and /events/:slug pages, plus the legacy /e slug).
    const isPublicEvent =
      location === "/events" ||
      location.startsWith("/events/");
    if (
      isNative &&
      !location.includes("/quiz") &&
      !location.startsWith("/onboarding") &&
      !location.startsWith("/invite/") &&
      !isPublicEvent
    ) {
      return <StartPage />;
    }


    // The app root still shows the quiz to signed-out visitors. `/quiz` itself
    // deliberately does NOT short-circuit here any more — it goes through the
    // router below so its tree is the same in both auth states.
    if (location === "/") {
      return <OnboardingRoute route="root" />;
    }

    // All other routes: fall through to render the full route layout
    // The sign-in overlay will be shown by ProtectedRoute / auto-open effect
  }

  return (
    <Router base={basePath}>
      <ConversationsProvider>
        <div
          className={`flex flex-col ${
            routeOwnsViewport ? "h-app-viewport overflow-hidden" : "min-h-dvh"
          }`}
        >
          {!onboardingOwnsBackground && (
            <ParticleBackground className="fixed inset-0" />
          )}
          {!onboardingHidesChrome && isSignedIn && <Navigation />}
          {/* Renders only with `?vpdebug=1` in the URL. */}
          <ViewportDebug />

          {/* `md:pt-16` is the room the fixed `Navigation` occupies on
              desktop. Onboarding doesn't render it (see
              `onboardingHidesChrome`), so on a wide window that padding was
              64px of nothing above a flow whose first screen is meant to start
              at the top of the page. */}
          <div
            className={`flex-1 basis-[0] flex flex-col relative ${
              routeOwnsViewport ? "min-h-0" : ""
            } ${onboardingHidesChrome ? "" : "md:pt-16"}`}
          >
            {/* Signup owns its own insets, so the route wrapper stays out of
                it entirely — see the flows' own bars. Full-screen routes are
                the same case for the same reason: their bars are pinned to the
                edges of the window and have to pad *inside* themselves, or the
                blur under the status bar becomes a bare strip above them. */}
            <NativeAppInsets
              insets={!onboardingHidesChrome && !routeOwnsViewport}
              fill={routeOwnsViewport}
            >
              <Switch>
                <TitledRoute path="/terms" title="Terms of Service - Outclose">
                  <Terms />
                </TitledRoute>
                <TitledRoute path="/privacy" title="Privacy Policy - Outclose">
                  <Privacy />
                </TitledRoute>
                <TitledRoute path="/community-guidelines" title="Community Guidelines - Outclose">
                  <CommunityGuidelines />
                </TitledRoute>
                <TitledRoute path="/help" title="Help Center - Outclose">
                  <Help />
                </TitledRoute>

                {/* Default route - shows dashboard */}
                <TitledRoute path="/" title="Dashboard - Outclose">
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute path="/onboarding" title="Get started - Outclose">
                  <OnboardingRoute route="onboarding" />
                </TitledRoute>

                <TitledRoute path="/quiz" title="Your answers - Outclose">
                  <OnboardingRoute route="quiz" />
                </TitledRoute>

                <TitledRoute path="/dashboard" title="Dashboard - Outclose">
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute path="/connections" title="Connections - Outclose">
                  <ProtectedRoute>
                    <Connections />
                  </ProtectedRoute>
                </TitledRoute>

                {/* Guided post-signup booking (experiment) — static segment,
                    must precede the /booking/:bookingId param route. */}
                <TitledRoute
                  path="/booking/start"
                  title="Book your first event - Outclose"
                >
                  <ProtectedRoute>
                    <BookingStart />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute
                  path="/booking/:bookingId"
                  title="Booking Details - Outclose"
                >
                  <ProtectedRoute>
                    <BookingDetail />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute path="/booking" title="Booking - Outclose">
                  <ProtectedRoute>
                    <Booking />
                  </ProtectedRoute>
                </TitledRoute>

                {/* +1 invite landing — public for unauth users (shows
                    preview + sign-in CTA); accept requires auth. */}
                <TitledRoute path="/invite/:code" title="You're invited - Outclose">
                  <InviteAccept />
                </TitledRoute>

                {/* Kiki detail page — stands on its own id; must come before /events/:slug. */}
                <TitledRoute path="/kiki/:id" title="Kiki - Outclose">
                  <KikiPage />
                </TitledRoute>

                {/* Host event creation — consumer-facing UI for users to
                    create and share their own events. */}
                <TitledRoute path="/events/new" title="Create an event - Outclose">
                  <ProtectedRoute>
                    <HostEventCreate />
                  </ProtectedRoute>
                </TitledRoute>

                {/* Owner edit — same form pre-filled with the existing event. */}
                <TitledRoute path="/events/:slug/edit" title="Edit event - Outclose">
                  <ProtectedRoute>
                    <HostEventCreate />
                  </ProtectedRoute>
                </TitledRoute>

                <Route path="/events/:slug/interested">
                  <ProtectedRoute>
                    <EventInterested />
                  </ProtectedRoute>
                </Route>

                {/* Legacy slugged kiki URL — still resolves by request id. */}
                <TitledRoute path="/events/:slug/kiki/:requestId" title="Kiki - Outclose">
                  <KikiPage />
                </TitledRoute>

                {/* Public event page — also mounted on the marketing domain.
                    Mirrored here so authenticated users hitting /events/{slug} on
                    the app domain see the page instead of a 404. */}
                <TitledRoute path="/events/:slug" title="Event - Outclose">
                  <EventPublic />
                </TitledRoute>

                {/* Public events directory — same rationale as /events/:slug. */}
                <TitledRoute path="/events" title="Events - Outclose">
                  <EventsIndex />
                </TitledRoute>

                {/* Clubs (clubs_tab experiment) — audience-scoped communities with a
                    forum, member polls and per-city meetups. */}
                <TitledRoute path="/clubs/:slug/posts/:postId" title="Club - Outclose">
                  <ProtectedRoute>
                    <ClubPostThread />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute path="/clubs/:slug/forum" title="Club forum - Outclose">
                  <ProtectedRoute>
                    <ClubForum />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute path="/clubs/:slug" title="Club - Outclose">
                  <ProtectedRoute>
                    <ClubDetail />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute path="/clubs" title="Clubs - Outclose">
                  <ProtectedRoute>
                    <ClubsIndex />
                  </ProtectedRoute>
                </TitledRoute>

                {/* Create a new group (requires auth) — must come before /g/:slug. */}
                <TitledRoute path="/g/new" title="New Group - Outclose">
                  <ProtectedRoute>
                    <GroupEdit />
                  </ProtectedRoute>
                </TitledRoute>

                {/* Edit an existing group — must come before /g/:slug. */}
                <TitledRoute path="/g/:slug/edit" title="Edit Group - Outclose">
                  <ProtectedRoute>
                    <GroupEdit />
                  </ProtectedRoute>
                </TitledRoute>

                {/* Public group page — mirrored for authenticated users. */}
                <TitledRoute path="/g/:slug" title="Group - Outclose">
                  <GroupPublic />
                </TitledRoute>

                <TitledRoute path="/profile" title="Profile - Outclose">
                  <Profile />
                </TitledRoute>

                <TitledRoute path="/profile/edit" title="Edit profile - Outclose">
                  <EditProfile />
                </TitledRoute>

                <TitledRoute path="/profile/personality" title="Connection Profile - Outclose">
                  <EditPersonality />
                </TitledRoute>

                <TitledRoute path="/profile/settings" title="Settings - Outclose">
                  <Settings />
                </TitledRoute>

                <TitledRoute path="/profile/notifications" title="Notifications - Outclose">
                  <NotificationSettings />
                </TitledRoute>

                <TitledRoute
                  path="/chat/:conversationId"
                  title="Chat - Outclose"
                >
                  <ProtectedRoute>
                    <Chat />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute path="/chat" title="Chat - Outclose">
                  <ProtectedRoute>
                    <Chat />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute
                  path="/dinner-survey/:bookingId"
                  title="Post-Dinner Survey - Outclose"
                >
                  <ProtectedRoute>
                    <PostDinnerSurvey />
                  </ProtectedRoute>
                </TitledRoute>

                <TitledRoute
                  path="/poll/:pollId"
                  title="Poll - Outclose"
                >
                  <ProtectedRoute>
                    <PollDetail />
                  </ProtectedRoute>
                </TitledRoute>

                {/* 404 */}
                <Route>
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold text-primary-500 mb-4">
                        {t('common.404.title')}
                      </h1>
                      <p className="text-dark-muted">{t('common.404.message')}</p>
                      <p className="text-neutral-500">{location}</p>
                    </div>
                  </div>
                </Route>
              </Switch>
            </NativeAppInsets>
          </div>
        </div>
      </ConversationsProvider>
    </Router>
  );
}

export default MainApp;
